<template>
	<div v-if="!hideSummary"
	  id="summary"
	  ref="summary"
	  class="summary"
	  :style="{ height, transition }"
	>
	  <div class="row">
		<div class="col-12 mb-4">
		  <h3 class="title-4 text-grey-800 mb-2">
			{{ stepNum }}{{ store.state.content.planningSummary_sectionTitle }}
		  </h3>
		  <div
			class="summary-wrapper bg-white px-3 pt-3 pb-2 p-sm-4 planning-bottom mt-4 shadow-4"
		  >
			<Accommodations />
			<BorderHorizontal />
			<!-- ---------------------- -->
			<Options :variant="anySelectedVariant" />
			<BorderHorizontal />
			<!-- ---------------------- -->
			<Total />
			<Fees />
			<BottomButtons />
		  </div>
		</div>
	  </div>
	  <StickySummary />
	</div>
  </template>
  
  <script lang="ts" setup>
  import { ref, watch, onMounted, computed, toRef, nextTick } from "vue";
  import { useStore } from "vuex";
  import availableTransportPackCodes from "@/vue/planning/computed/availableTransportPackCodes";
  
  // components
  import Fees from "@/vue/planning/components/summary/fees.vue";
  import Accommodations from "@/vue/planning/components/summary/accommodations.vue";
  import BorderHorizontal from "@/vue/common/borderHorizontal.vue";
  import Options from "@/vue/planning/components/summary/options.vue";
  import Total from "@/vue/planning/components/summary/total.vue";
  import BottomButtons from "@/vue/planning/components/summary/bottomButtons.vue";
  import StickySummary from "@/vue/planning/components/stickySummary.vue";
  
  const store = useStore();
  
  // define props (transition only)
  const props = withDefaults(
	defineProps<{ transition?: string }>(),
	{ transition: "height 250ms ease-in-out" }
  );
  
  // keep `transition` reactive using toRef
  const transition = toRef(props, "transition");
  
  // if there is no product available, don't show the summary
  const hideSummary = computed(() => {
	return Object.keys(store.getters.wiypCall).length === 0 || store.getters.totalSelectedHebergements === 0;
  });

  // compute the step number (3. or 4.)
  const stepNum = computed(() => {
	return (
	  availableTransportPackCodes.value.length > 0 &&
	  store.state.screenType === "desktop"
	)
	  ? "4. "
	  : "3. ";
  });
  
  // compute the first selected prestation variant
  const anySelectedVariant = computed(() => {
	const selected = store.getters.selectedPrestations;
	return selected && selected.length > 0 ? selected[0].prestation : undefined;
  });
  
  // reactive height for accordion
  const height = ref<string>("0px");
  const summary = ref<HTMLElement | null>(null);
  
  function expand() {
	if (!summary.value) return;
	height.value = summary.value.scrollHeight + "px";
	const onEnd = () => {
	  summary.value!.removeEventListener("transitionend", onEnd);
	  height.value = "";
	};
	summary.value.addEventListener("transitionend", onEnd);
  }
  
  function collapse() {
	height.value = "0px";
  }
  
  // group together the related watchers to handle summary expand/collapse
  watch(
	[
	  () => store.getters.totalSelectedHebergements,
	  () => Object.keys(store.getters.wiypCall).length > 0
	],
	async ([newHebergements, wiypReady], [oldHebergements, oldWiypReady]) => {
	  // Check if summary should expand
	  if (wiypReady && (!oldWiypReady || (newHebergements > 0 && oldHebergements === 0))) {
		await nextTick();
		expand();
	  }
	  // Check if summary should collapse
	  else if (!wiypReady || (newHebergements === 0 && oldHebergements > 0)) {
		collapse();
	  }
	},
	{ immediate: true }
  );
  
  // initialize collapsed on mount
  // onMounted(() => collapse());
  </script>
  
  <style scoped>
  .summary {
	overflow: hidden;
  }
  .summary-wrapper {
	box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
	border-radius: var(--border-radius-card-secondary);
	padding: 35px;
  }
  </style>
  